import { AxiosResponse } from 'axios'

import { AtlasUserOverride } from 'api/atlasClient'
import {
    CerebroApiOptions,
    CerebroPaginatedResponse,
    CerebroResourceResponse,
    PaginatedResponse,
} from 'api/Cerebro/types'
import {
    Expression,
    HulkPaginatedEnvelope,
    HulkQuery,
    HulkQuerySortExpression,
    HulkQueryTimeComparison,
    HulkQueryTopNConfig,
    Operator,
    queryHulkDatasource,
    toCerebroPaginatedEnvelope,
} from 'api/hulkApi'
import { orgScopedApiClient } from 'api/orgScopedClient'
import { date } from 'configuration/dayjs'
import {
    ADVANCED_PRODUCT_TITLE,
    CONTAINS,
    DATES,
    DOES_NOT_CONTAIN,
    EQUALS,
    getBooleanFilterValue,
    GREATER_THAN,
    GREATER_THAN_OR_EQUAL,
    INSENSITIVE_EXACT,
    LESS_THAN,
    LESS_THAN_OR_EQUAL,
    MARKET_ANALYSIS_SEGMENTS,
    METRIC_FILTERS,
    NONE_OF,
    SEGMENT_BRANDS,
    SELLER_NAME,
    SOV_ASINS,
} from 'const/filters'
import {
    DataSource,
    DataSourceGroupBy,
    DataSourceMetric,
} from 'features/MetricsGlossary/types'
import {
    clampEndToWeeklyDataBoundary,
    snapToFullWeeks,
} from 'helpers/dateRange'
import { getSingleClauseFilterValue } from 'helpers/filter'
import { isNonEmptyArray } from 'helpers/typeGuard'
import { formatDateFromFilter } from 'helpers/utils'
import {
    BooleanFilter,
    BooleanLeafFilter,
    FilterOption,
    FiltersState,
    MetricFilter,
    Report,
} from 'types'
import queryClientRegistry from 'utilities/queryClientRegistry'

import {
    buildMarketAnalysisSegmentBaseFilters,
    intersectExpressions,
} from './marketAnalysisSegmentsFilters'

export const MARKET_ANALYSIS_SEGMENTS_DATA_SOURCE = 'sales_estimates'
export const MARKET_ANALYSIS_SEGMENTS_DATE_PREFIX = 'report_date'

interface MarketAnalysisSegmentsFilterParams {
    report_date_min?: string
    report_date_max?: string
    segment_id__in?: string
    asin__in?: string
    title_filter?: BooleanFilter
    brand__in?: string
    brand__not_in?: string
    seller_id__in?: string
    seller_id__not_in?: string
    metric_filters?: MetricFilter[]
}

function stringifyFilterOptions(options: FilterOption[]): string {
    return options.map((item) => item.value).join(',')
}

export function formatMarketAnalysisSegmentsFilters(
    filters: FiltersState,
    _hasHourlyData = false,
    fiscalYearStartMonth?: number
): MarketAnalysisSegmentsFilterParams {
    const params: MarketAnalysisSegmentsFilterParams = {}

    const dates = filters[DATES]
    const { start, end } = formatDateFromFilter(
        dates,
        date(),
        filters.rangeLag,
        fiscalYearStartMonth
    )
    params.report_date_min = start
    // hulk aligns the window to whole weeks but has no notion of the last
    // complete one, so a stored range ending past the boundary (freely
    // selectable before the picker was constrained) would align outward into an
    // empty trailing week — and the comparison would then be measured against a
    // fully-populated prior period. Clamp, don't snap: the dates otherwise pass
    // through untouched.
    params.report_date_max = clampEndToWeeklyDataBoundary(start, end)

    const segments = filters[MARKET_ANALYSIS_SEGMENTS]
    if (isNonEmptyArray(segments)) {
        params.segment_id__in = stringifyFilterOptions(segments)
    }

    const asins = filters[SOV_ASINS]
    if (isNonEmptyArray(asins)) {
        params.asin__in = stringifyFilterOptions(asins)
    }

    const titleOption = filters[ADVANCED_PRODUCT_TITLE]?.[0]
    if (titleOption) {
        const decoded = getBooleanFilterValue(titleOption)
        if (decoded.and && decoded.and.length > 0) {
            params.title_filter = decoded
        }
    }

    const brands = filters[SEGMENT_BRANDS]
    if (isNonEmptyArray(brands)) {
        const clause = getSingleClauseFilterValue(brands)
        if (clause && clause.value.length > 0) {
            const key =
                clause.operator === NONE_OF ? 'brand__not_in' : 'brand__in'
            params[key] = clause.value.join(',')
        }
    }

    // The seller filter typeahead stores `seller_id` values (with seller
    // names shown only as labels), so the widget query filters by id.
    const sellerIds = filters[SELLER_NAME]
    if (isNonEmptyArray(sellerIds)) {
        const clause = getSingleClauseFilterValue(sellerIds)
        if (clause && clause.value.length > 0) {
            const key =
                clause.operator === NONE_OF
                    ? 'seller_id__not_in'
                    : 'seller_id__in'
            params[key] = clause.value.join(',')
        }
    }

    // Unlike Banner-backed data sources, MAS targets hulk directly and
    // converts metric filters into hulk FieldPredicates downstream, so the
    // filter array is passed through structured rather than flattened to
    // `metric__op=value` URL params.
    const metricFilters = filters[METRIC_FILTERS]
    if (isNonEmptyArray(metricFilters)) {
        params.metric_filters = metricFilters
    }

    return params
}

function splitCsv(value: unknown): string[] {
    if (typeof value !== 'string' || value.length === 0) {
        return []
    }
    return value.split(',').filter(Boolean)
}

// Hulk and Forge use different operation suffixes on metric names. Forge
// appends Forge-shaped suffixes (__last_period, __change, …) to outgoing
// request metric names; hulk speaks its own (__comparison_period, __delta, …).
// Translate both directions so the request reaches hulk in its native shape
// and the response records read back in the shape charts/tables expect.
//
// Longer suffixes must come first so the matcher prefers `__total__last_period`
// over `__last_period`.
const FORGE_TO_HULK_SUFFIXES: ReadonlyArray<readonly [string, string]> = [
    ['__total__last_period', '__comparison_period_total'],
    ['__total__change', '__total_delta'],
    ['__total__percentage_change', '__total_delta_pct'],
    ['__last_period', '__comparison_period'],
    ['__change', '__delta'],
    ['__percentage_change', '__delta_pct'],
]

const HULK_TO_FORGE_SUFFIXES: ReadonlyArray<readonly [string, string]> = [
    ['__comparison_period_total', '__total__last_period'],
    ['__total_delta', '__total__change'],
    ['__total_delta_pct', '__total__percentage_change'],
    ['__comparison_period', '__last_period'],
    ['__delta', '__change'],
    ['__delta_pct', '__percentage_change'],
]

function rewriteSuffix(
    name: string,
    map: ReadonlyArray<readonly [string, string]>
): string {
    for (const [from, to] of map) {
        if (name.endsWith(from)) {
            return name.slice(0, -from.length) + to
        }
    }
    return name
}

function toHulkMetricName(name: string): string {
    return rewriteSuffix(name, FORGE_TO_HULK_SUFFIXES)
}

function translateMetricsForHulk(metrics: string[]): string[] {
    const result = new Set<string>()
    for (const name of metrics) {
        result.add(toHulkMetricName(name))
    }
    return Array.from(result)
}

// Rename hulk's product dimensions onto the fields ProductCatalogTitleRenderer
// reads as fallbacks (record.title, record.image_url).
const HULK_DIMENSION_ALIASES: Record<string, string> = {
    title_stable: 'title',
    image_url_hero: 'image_url',
}

// Hulk returns period-over-period change as a ratio (e.g. 1.0 = +100%) in its
// *_delta_pct fields, but Forge's delta formatters treat __percentage_change as
// an already-scaled percent. Scale ratios to percent at the boundary so the
// widget render paths (tables, metric widgets, timeseries) stay consistent. CSV
// export is unaffected: it posts to Hulk's export endpoint and never passes
// through translateHulkRecord.
const HULK_RATIO_SUFFIXES = ['__total_delta_pct', '__delta_pct'] as const

function isHulkRatioKey(key: string): boolean {
    return HULK_RATIO_SUFFIXES.some((suffix) => key.endsWith(suffix))
}

function scaleRatioToPercent(value: unknown): unknown {
    return typeof value === 'number' && Number.isFinite(value)
        ? value * 100
        : value
}

export function translateHulkRecord(
    record: Record<string, unknown>,
    marketplace: string | null
): Record<string, unknown> {
    const result: Record<string, unknown> = { ...record }
    for (const key of Object.keys(record)) {
        const translated = rewriteSuffix(key, HULK_TO_FORGE_SUFFIXES)
        if (translated !== key) {
            result[translated] = isHulkRatioKey(key)
                ? scaleRatioToPercent(record[key])
                : record[key]
        }
        const alias = HULK_DIMENSION_ALIASES[key]
        if (alias && result[alias] === undefined) {
            result[alias] = record[key]
        }
    }
    // Hulk responses don't include marketplace per row. Stamp the segments'
    // shared marketplace (uppercased to match CountryCode) so the product
    // renderer can produce a working product-detail link.
    if (marketplace && result.marketplace === undefined) {
        result.marketplace = marketplace.toUpperCase()
    }
    return result
}

function parseOrdering(value: unknown): HulkQuerySortExpression[] | undefined {
    if (typeof value !== 'string' || value.length === 0) {
        return undefined
    }
    const expressions = value
        .split(',')
        .filter(Boolean)
        .map((field) => {
            if (field.startsWith('-')) {
                return {
                    field: toHulkMetricName(field.slice(1)),
                    direction: 'desc' as const,
                }
            }
            return {
                field: toHulkMetricName(field),
                direction: 'asc' as const,
            }
        })
    return expressions.length > 0 ? expressions : undefined
}

interface WidgetRequestParams {
    metrics?: string
    group_by?: string
    ordering?: string
    limit?: number
    offset?: number
    segment_id__in?: string
    report_date_min?: string
    report_date_max?: string
    previous_period_report_date_min?: string
    previous_period_report_date_max?: string
    asin__in?: string
    title_filter?: BooleanFilter
    brand__in?: string
    brand__not_in?: string
    seller_id__in?: string
    seller_id__not_in?: string
    metric_filters?: MetricFilter[]
    auto_filter?: string | boolean
    auto_filter_limit?: number
    auto_filter_direction?: 'asc' | 'desc'
    auto_filter_dimension?: string
    auto_filter_metric?: string
    auto_filter_include_others?: boolean
    [key: string]: unknown
}

// MetricsFilterForm only emits the comparator subset of NUMBER_RANGE_OPERATOR_OPTIONS
// (gt/lt/gte/lte/exact); the rank_* operators are filtered out at the form
// level so they never reach this API.
const METRIC_FILTER_OPERATOR_TO_HULK: Record<string, Operator> = {
    [EQUALS]: '_eq',
    [GREATER_THAN]: '_gt',
    [LESS_THAN]: '_lt',
    [GREATER_THAN_OR_EQUAL]: '_gte',
    [LESS_THAN_OR_EQUAL]: '_lte',
}

function buildMetricFilterExpression(
    metricFilters: MetricFilter[] | undefined
): Expression | undefined {
    if (!metricFilters || metricFilters.length === 0) {
        return undefined
    }
    const predicates: Expression[] = []
    for (const { metric, operator, value } of metricFilters) {
        if (!metric || !operator || value == null) {
            continue
        }
        const hulkOperator = METRIC_FILTER_OPERATOR_TO_HULK[operator]
        if (!hulkOperator) {
            continue
        }
        predicates.push({
            field: toHulkMetricName(metric),
            operator: hulkOperator,
            value,
        })
    }
    return intersectExpressions(predicates)
}

const TITLE_FIELD = 'title_searchable'

// `title_searchable` is a normalized text column on hulk's `sales_estimates`
// datasource, so the case-insensitive Forge operators collapse onto their
// case-sensitive hulk counterparts. The MAS dashboard filter restricts the
// operator picker to these three options (see MarketAnalysisSegmentsFilters);
// anything else here would mean the UI drifted out of sync.
const TITLE_OPERATOR_TO_HULK: Record<string, Operator> = {
    [CONTAINS]: '_contains',
    [DOES_NOT_CONTAIN]: '_not_contains',
    [INSENSITIVE_EXACT]: '_eq',
}

function extractTitleKeywords(value: BooleanLeafFilter['value']): string[] {
    if (typeof value === 'string') {
        return value.length > 0 ? [value] : []
    }
    if (!Array.isArray(value)) {
        return []
    }
    return (value as unknown[]).filter(
        (v): v is string => typeof v === 'string' && v.length > 0
    )
}

function buildTitleLeafExpression(
    leaf: BooleanLeafFilter
): Expression | undefined {
    const hulkOperator = TITLE_OPERATOR_TO_HULK[leaf.operator]
    if (!hulkOperator) {
        return undefined
    }
    const keywords = extractTitleKeywords(leaf.value)
    if (keywords.length === 0) {
        return undefined
    }
    const predicates: Expression[] = keywords.map((keyword) => ({
        field: TITLE_FIELD,
        operator: hulkOperator,
        value: keyword,
    }))
    if (predicates.length === 1) {
        return predicates[0]
    }
    // Multi-keyword "Does not contain" excludes ANY of the listed keywords —
    // AND-combine so a row must miss all of them. Contains/exact OR-combine.
    const combinator = hulkOperator === '_not_contains' ? '_and' : '_or'
    return { [combinator]: predicates } as Expression
}

function buildTitleFilterExpression(
    titleFilter: BooleanFilter | undefined
): Expression | undefined {
    if (!titleFilter?.and || titleFilter.and.length === 0) {
        return undefined
    }
    const groupExpressions: Expression[] = []
    for (const group of titleFilter.and) {
        const leafExpressions: Expression[] = []
        for (const leaf of group.or ?? []) {
            const expr = buildTitleLeafExpression(leaf)
            if (expr) {
                leafExpressions.push(expr)
            }
        }
        if (leafExpressions.length === 1) {
            groupExpressions.push(leafExpressions[0])
        } else if (leafExpressions.length > 1) {
            groupExpressions.push({ _or: leafExpressions })
        }
    }
    return intersectExpressions(groupExpressions)
}

function buildUserFilterExpression(
    params: WidgetRequestParams
): Expression | undefined {
    const predicates: Expression[] = []

    const asins = splitCsv(params.asin__in)
    if (asins.length > 0) {
        predicates.push({ field: 'asin', operator: '_in', value: asins })
    }

    const titleExpr = buildTitleFilterExpression(params.title_filter)
    if (titleExpr) {
        predicates.push(titleExpr)
    }

    const brandsIn = splitCsv(params.brand__in)
    if (brandsIn.length > 0) {
        predicates.push({
            field: 'brand_stable',
            operator: '_in',
            value: brandsIn,
        })
    }
    const brandsNotIn = splitCsv(params.brand__not_in)
    if (brandsNotIn.length > 0) {
        predicates.push({
            field: 'brand_stable',
            operator: '_not_in',
            value: brandsNotIn,
        })
    }

    const sellersIn = splitCsv(params.seller_id__in)
    if (sellersIn.length > 0) {
        predicates.push({
            field: 'seller_id',
            operator: '_in',
            value: sellersIn,
        })
    }
    const sellersNotIn = splitCsv(params.seller_id__not_in)
    if (sellersNotIn.length > 0) {
        predicates.push({
            field: 'seller_id',
            operator: '_not_in',
            value: sellersNotIn,
        })
    }

    return intersectExpressions(predicates)
}

function buildTopNConfig(
    params: WidgetRequestParams,
    queryDimensions: string[]
): HulkQueryTopNConfig | undefined {
    if (!params.auto_filter) {
        return undefined
    }
    // top_n must group by the same dimensions as the outer query. The widget's
    // auto_filter_dimension carries the selected group by plus its
    // groupByDependencies; keep only the ones that made it into the query so
    // top_n lines up with what hulk receives. Intersecting drops the synthetic
    // product_info placeholder (absent from queryDimensions) without naming it,
    // and excludes the date dimension on time-series widgets.
    const dimensions = splitCsv(params.auto_filter_dimension).filter((d) =>
        queryDimensions.includes(d)
    )
    const metric = params.auto_filter_metric
    const n = params.auto_filter_limit
    if (dimensions.length === 0 || !metric || n == null) {
        return undefined
    }
    return {
        n,
        dimensions,
        ordering: [
            {
                field: toHulkMetricName(metric),
                direction: params.auto_filter_direction ?? 'desc',
            },
        ],
        include_others: params.auto_filter_include_others ?? false,
    }
}

function emptyEnvelope(): CerebroPaginatedResponse<Record<string, unknown>> {
    return {
        data: { count: 0, next: null, previous: null, results: [] },
        status: 200,
        statusText: 'OK',
        headers: {},
        config: {} as never,
    }
}

interface WidgetApiContext {
    limit?: number
    include_change_metrics?: boolean
    period_delta_type?: 'prior_period' | 'prior_year' | 'custom'
}

// Cap unbounded queries: TABLE widgets pass an explicit limit; everything
// else (timeseries / categorical / metric / pie) ships no limit and would
// otherwise return the full join. Bound it so chart rendering stays sane.
const DEFAULT_ROW_CAP = 1000

function buildTimeComparison(
    widget: WidgetApiContext,
    params: WidgetRequestParams
): HulkQueryTimeComparison | undefined {
    if (!widget.include_change_metrics) {
        return undefined
    }
    if (widget.period_delta_type === 'prior_year') {
        return { mode: 'shift', unit: 'year', amount: 1 }
    }
    if (widget.period_delta_type === 'custom') {
        // The dates the user picked arrive as previous_period_* params
        // (formatPeriodDeltaDateRange). hulk takes an explicit window, so send
        // it rather than quietly comparing against the prior period instead.
        const start = params.previous_period_report_date_min
        const end = params.previous_period_report_date_max
        if (start && end) {
            return { mode: 'explicit', start, end }
        }
    }
    return { mode: 'prior_period' }
}

interface BuiltHulkQuery {
    query: HulkQuery
    marketplace: string | null
}

// The widget data path receives the print page's impersonation override as
// cerebro-shaped option headers (the same ones banner data sources forward).
// Translate them into the Atlas override that the token mint and the
// hulk/segment requests understand. Returns undefined for ordinary,
// non-impersonated requests so they keep the default identity path.
export function toAtlasUserOverride(
    options?: CerebroApiOptions
): AtlasUserOverride | undefined {
    const headers = options?.headers
    if (!headers) {
        return undefined
    }
    const { userId, orgId, orgGroupId } = headers
    if (!userId && !orgId && !orgGroupId) {
        return undefined
    }
    return {
        userId: userId ?? undefined,
        orgId: orgId ?? undefined,
        orgGroupId: orgGroupId ?? undefined,
    }
}

async function buildMarketAnalysisSegmentsHulkQuery(
    widget: WidgetApiContext,
    params: WidgetRequestParams,
    { paginate }: { paginate: boolean },
    override?: AtlasUserOverride
): Promise<BuiltHulkQuery | null> {
    const segmentIds = splitCsv(params.segment_id__in)
    // Refuse to fire unscoped queries — an unfiltered sales_estimates query
    // would return the entire dataset.
    if (segmentIds.length === 0) {
        return null
    }
    const { expression: baseFilters, marketplace } =
        await buildMarketAnalysisSegmentBaseFilters(segmentIds, override)
    const userFilters = buildUserFilterExpression(params)
    const metricFilters = buildMetricFilterExpression(params.metric_filters)
    const filters = intersectExpressions([
        baseFilters,
        userFilters,
        metricFilters,
    ])

    const start = params.report_date_min
    const end = params.report_date_max
    const comparison = buildTimeComparison(widget, params)
    const time =
        start || end || comparison
            ? {
                  start: start ?? undefined,
                  end: end ?? undefined,
                  comparison,
                  // sales_estimates is weekly (Sun–Sat) and filters on the week's
                  // Sunday key, so a window that doesn't cover whole weeks drops or
                  // adds a week's revenue without saying so. Let hulk widen it to
                  // whole weeks — one authority, and the resolved window comes back
                  // on `meta.time_range` for the widget to display.
                  align_to_grain: true,
              }
            : undefined

    // `product_info` is a synthetic placeholder group by — its real
    // dimensions (asin, title_stable, image_url_hero) are appended via
    // groupByDependencies. Drop the placeholder before hitting hulk.
    const dimensions = splitCsv(params.group_by).filter(
        (d) => d !== 'product_info'
    )
    const metrics = translateMetricsForHulk(splitCsv(params.metrics))
    const ordering = parseOrdering(params.ordering)

    const topN = buildTopNConfig(params, dimensions)

    // When top_n drives the result, skip pagination — it would clip into the
    // top-N + others rollup unpredictably. Exports also skip pagination so
    // the server-side row limit is the only cap.
    const limit = params.limit ?? widget.limit ?? DEFAULT_ROW_CAP
    const offset = params.offset ?? 0
    const pagination =
        !paginate || topN
            ? undefined
            : {
                  limit,
                  offset,
                  include_count: true,
              }

    const query: HulkQuery = {
        dimensions,
        metrics,
        filters,
        time,
        ordering,
        pagination,
        top_n: topN,
    }
    return { query, marketplace }
}

export async function getMarketAnalysisSegmentsWidgetData(
    widget: WidgetApiContext,
    params: WidgetRequestParams,
    options?: CerebroApiOptions
): Promise<HulkPaginatedEnvelope<Record<string, unknown>>> {
    const override = toAtlasUserOverride(options)
    const built = await buildMarketAnalysisSegmentsHulkQuery(
        widget,
        params,
        { paginate: true },
        override
    )
    if (!built) {
        return emptyEnvelope()
    }
    const { query, marketplace } = built

    const response = await queryHulkDatasource<Record<string, unknown>>(
        MARKET_ANALYSIS_SEGMENTS_DATA_SOURCE,
        query,
        override
    )
    const translatedResponse = {
        ...response,
        data: {
            ...response.data,
            data: response.data.data.map((record) =>
                translateHulkRecord(record, marketplace)
            ),
        },
    }
    return toCerebroPaginatedEnvelope(translatedResponse)
}

interface ExportParams extends WidgetRequestParams {
    async_download_name?: string
}

// Reads the metric/group_by glossary that any widget on the current page
// already loaded into react-query (via useGetDataSourceMetricsColumns /
// useGetDataSourceGroupBysColumns) and builds {field_name → display_name} so
// the exported CSV uses metric-glossary labels as column headers instead of
// raw hulk field names. metadata_all_names covers aliases so historical names
// map to the current label. Filters by the sales_estimates DataSource's slug
// (resolved from `dataSourceList` cache, not assumed equal to the metadata
// key) so a name collision across datasources can't bleed in the wrong label.
function readSalesEstimatesFieldLabels(): Record<string, string> {
    const queryClient = queryClientRegistry.getClient()
    if (!queryClient) {
        return {}
    }
    const cachedResults = <T>(queryKey: string): Array<[unknown, T[]]> =>
        queryClient
            .getQueriesData<AxiosResponse<PaginatedResponse<T>> | null>({
                queryKey: [queryKey],
            })
            .map(([key, value]) => [key, value?.data.results ?? []])

    const slug = cachedResults<DataSource>('dataSourceList')
        .flatMap(([, results]) => results)
        .find(
            (d) => d.metadata_key === MARKET_ANALYSIS_SEGMENTS_DATA_SOURCE
        )?.slug
    if (!slug) {
        return {}
    }

    const matchingSlug = (key: unknown): boolean =>
        Array.isArray(key) &&
        (key[1] as { dataSourceSlug?: string } | undefined)?.dataSourceSlug ===
            slug

    const labels: Record<string, string> = {}
    const stamp = (names: string[], label: string): void => {
        for (const name of names) {
            if (name) {
                labels[name] = label
            }
        }
    }
    for (const [key, results] of cachedResults<DataSourceMetric>(
        'dataSourceMetricsList'
    )) {
        if (!matchingSlug(key)) {
            continue
        }
        for (const m of results) {
            stamp(
                [m.metadata_key, ...(m.metadata_all_names ?? [])],
                m.display_name
            )
            // Derived metrics (`__change`, `__last_period`, `__pct_of_total`)
            // live on each parent metric's `derived_metrics` collection and
            // carry their own display_name. Without stamping them, the export
            // falls back to raw hulk field names for any non-base column.
            for (const d of m.derived_metrics ?? []) {
                stamp([d.metadata_key], d.display_name)
            }
        }
    }
    for (const [key, results] of cachedResults<DataSourceGroupBy>(
        'dataSourceGroupBysList'
    )) {
        if (!matchingSlug(key)) {
            continue
        }
        for (const g of results) {
            stamp(
                [g.metadata_key, ...(g.metadata_all_names ?? [])],
                g.display_name
            )
        }
    }
    return labels
}

function declaredExportDateRange(params: ExportParams): {
    report_date_min?: string
    report_date_max?: string
} {
    const { report_date_min: min, report_date_max: max } = params
    if (!min || !max) {
        return { report_date_min: min, report_date_max: max }
    }
    const [start, end] = snapToFullWeeks(
        date(min, 'YYYY-MM-DD'),
        date(max, 'YYYY-MM-DD')
    )
    return {
        report_date_min: start.format('YYYY-MM-DD'),
        report_date_max: end.format('YYYY-MM-DD'),
    }
}

export async function getMarketAnalysisSegmentsExport(
    widget: WidgetApiContext,
    params: ExportParams
): Promise<CerebroResourceResponse<Report>> {
    const built = await buildMarketAnalysisSegmentsHulkQuery(widget, params, {
        paginate: false,
    })
    if (!built) {
        throw new Error('Select at least one segment to export.')
    }
    const allLabels = readSalesEstimatesFieldLabels()
    const dimensionFields = (built.query.dimensions ?? []).map((d) =>
        typeof d === 'string' ? d : d.name
    )
    const queryFields = [...dimensionFields, ...(built.query.metrics ?? [])]
    const fieldLabels: Record<string, string> = {}
    for (const field of queryFields) {
        const label = allLabels[field]
        if (label) {
            fieldLabels[field] = label
        }
    }
    const query: HulkQuery = {
        ...built.query,
        formatting: {
            ...built.query.formatting,
            field_labels: {
                ...built.query.formatting?.field_labels,
                ...fieldLabels,
            },
        },
    }
    return orgScopedApiClient.post('/api/hulk/exports/', {
        format: 'csv',
        queries: [
            {
                datasource: MARKET_ANALYSIS_SEGMENTS_DATA_SOURCE,
                query,
            },
        ],
        // Falls back to a descriptive default — Cerebro would otherwise label
        // the row "export" for unnamed widgets.
        async_download_name:
            params.async_download_name || 'Market Analysis Segments',
        // Surfaced top-level (in addition to the nested query.time) so the
        // Reports Drawer's renderReportDateRange picks them up from
        // request_params. Cerebro stores the whole body verbatim, atlas ignores
        // unknown top-level keys, so this is safe to send.
        //
        // Declared metadata, not a query — nothing comes back to correct it, so
        // it has to be snapped client-side to the same whole weeks hulk aligns
        // the query to. Otherwise the drawer states 7/1 – 7/31 while the file
        // holds 6/28 – 8/1.
        ...declaredExportDateRange(params),
    })
}
